import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopBar from "../components/TopBar";
import {
  FaUsers,
  FaBlog,
  FaChartLine,
  FaPlus,
  FaEnvelopeOpenText,
} from "react-icons/fa";
import axios from "axios";

const Dashboard = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBlogs: 0,
    siteVisitors: 0,
    todayVisitors: 0,
  });

  const [recentUsers, setRecentUsers] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setStats(res.data);
      setRecentUsers(res.data.recentUsers || []);
      setRecentBlogs(res.data.recentBlogs || []);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f4f6f9" }}>
      <Sidebar setIsSidebarOpen={setIsSidebarOpen} />

      <div
        style={{
          flex: 1,
          marginLeft: isSidebarOpen ? "250px" : "80px",
          padding: "25px",
          transition: "0.3s",
        }}
      >
        <TopBar />

        {/* HEADER */}
        <div style={{ marginBottom: "25px" }}>

          <p style={{ color: "#6c757d", marginBottom: 0 }} className="mt-3">
            Welcome back! Here's what's happening today.
          </p>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* ================= STAT CARDS ================= */}
        <div className="dashboard-grid">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<FaUsers />}
            onClick={() => navigate("/admin/users")}
            loading={loading}
          />

          <StatCard
            title="Total Blogs"
            value={stats.totalBlogs}
            icon={<FaBlog />}
            onClick={() => navigate("/admin/AdminBlogs")}
            loading={loading}
          />

          <StatCard
            title="Today's Visitors"
            value={stats.todayVisitors}
            icon={<FaChartLine />}
            onClick={() => navigate("/")}
            loading={loading}
          />

          <StatCard
            title="Total Visitors"
            value={stats.siteVisitors}
            icon={<FaChartLine />}
            onClick={() => navigate("/")}
            loading={loading}
          />
        </div>

        {/* ================= QUICK ACTIONS ================= */}
        <SectionCard title="Quick Actions">
          <div className="quick-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/admin/AdminBlogs")}
            >
              <FaPlus /> Add Blog
            </button>

            <button
              className="btn btn-success"
              onClick={() => navigate("/admin/users")}
            >
              <FaPlus /> Add User
            </button>

            {userRole === "superadmin" && (
              <button
                className="btn btn-warning"
                onClick={() => navigate("/admin/notifications")}
              >
                <FaEnvelopeOpenText /> View All Messages
              </button>
            )}
          </div>
        </SectionCard>

        {/* ================= RECENT ACTIVITY ================= */}
        <div className="activity-grid">
          <ActivityCard
            title="Recent Users"
            data={recentUsers}
            loading={loading}
          />

          <ActivityCard
            title="Recent Blogs"
            data={recentBlogs}
            loading={loading}
          />
        </div>
      </div>

      {/* ================= STYLES ================= */}
      <style>{`
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .activity-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 20px;
          margin-top: 30px;
        }

        .quick-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
        }

        @media (max-width: 768px) {
          .activity-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

/* ================= STAT CARD ================= */

const StatCard = ({ title, value, icon, onClick, loading }) => (
  <div
    onClick={onClick}
    style={{
      background: "#ffffff",
      padding: "22px",
      borderRadius: "14px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
      transition: "all 0.25s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-5px)";
      e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.08)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
    }}
  >
    <div>
      <p style={{ margin: 0, color: "#6c757d", fontSize: "14px" }}>{title}</p>
      <h3 style={{ margin: "8px 0 0 0", fontWeight: 600 }}>
        {loading ? "..." : value ?? 0}
      </h3>
    </div>
    <div style={{ fontSize: "24px", color: "#007bff" }}>{icon}</div>
  </div>
);

/* ================= SECTION WRAPPER ================= */

const SectionCard = ({ title, children }) => (
  <div
    style={{
      background: "#ffffff",
      padding: "22px",
      borderRadius: "14px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      marginBottom: "30px",
    }}
  >
    <h5 style={{ marginBottom: "18px", fontWeight: 600 }}>{title}</h5>
    {children}
  </div>
);

/* ================= ACTIVITY CARD ================= */

const ActivityCard = ({ title, data, loading }) => (
  <div
    style={{
      background: "#ffffff",
      padding: "22px",
      borderRadius: "14px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "15px",
      }}
    >
      <h5 style={{ fontWeight: 600, margin: 0 }}>{title}</h5>
    </div>

    {loading ? (
      <p>Loading...</p>
    ) : data.length === 0 ? (
      <p style={{ color: "#6c757d" }}>No recent data</p>
    ) : (
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {data.slice(0, 5).map((item, index) => (
          <li
            key={index}
            style={{
              padding: "10px 0",
              borderBottom:
                index !== data.length - 1 ? "1px solid #f1f1f1" : "none",
              fontSize: "14px",
            }}
          >
            {item.name || item.title}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default Dashboard;
